import { useEffect, useMemo, useRef, useState } from 'react';
import api from '../api';
import Modal from './Modal';
import ImageCropModal from './ImageCropModal';
import { useTranslation } from '../LanguageContext';
import { uploadImage } from '../utils/cloudflare';
import {
    FONTS, FONT_KEYS, PATTERNS, PRESETS, ProfileBackdrop,
    applyPreset, ensureFont, resolveTheme, themeVars,
} from '../utils/profileTheme';

/**
 * The appearance editor.
 *
 * Two things decide the shape of this screen.
 *
 * **The preview is the same code as the page.** It renders `ProfileBackdrop`
 * and the `pf-*` classes off the working draft, so it is not a drawing of the
 * result — it is the result, at a smaller size. A mock-up would drift from the
 * real page the first time either changed, and the whole reason anyone opens
 * this is to find out what their profile will look like.
 *
 * **Nothing is saved until it is saved.** Every control edits a local draft, so
 * a half-finished experiment never reaches the public profile, and Cancel is a
 * real escape rather than a prompt to undo eleven changes by hand. The one
 * exception is the wallpaper upload, which has to reach Cloudflare to have a
 * URL at all — so it uploads on pick and is only *referenced* on save.
 *
 * The template gallery is first and opens selected because the controls behind
 * it are the part almost nobody wants to operate: choosing eleven colours that
 * work together is a design job, and a wall of sliders with no starting point
 * is how a customisation feature ends up used by nobody.
 */

const WALLPAPER_ASPECT = 9 / 16;

// ── Small controls ───────────────────────────────────────────────────────

function Field({ label, hint, children }) {
    return (
        <div className="mb-4">
            <div className="flex items-baseline justify-between gap-2 mb-1.5">
                <label className="text-xs font-bold text-slate-700">{label}</label>
                {hint && <span className="text-[10px] text-slate-400 tabular-nums">{hint}</span>}
            </div>
            {children}
        </div>
    );
}

/**
 * A colour. The swatch is the native picker (every platform's own, including
 * the phone ones) and the text box beside it takes a pasted hex — which is how
 * anyone matching an existing brand palette actually has the value.
 */
function ColorField({ label, value, onChange }) {
    const [text, setText] = useState(value);

    useEffect(() => { setText(value); }, [value]);

    const commit = (raw) => {
        setText(raw);
        const hex = raw.startsWith('#') ? raw : `#${raw}`;
        if (/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(hex)) onChange(hex.toLowerCase());
    };

    return (
        <Field label={label}>
            <div className="flex items-center gap-2">
                <input
                    type="color"
                    value={value}
                    onChange={(e) => { setText(e.target.value); onChange(e.target.value); }}
                    className="w-11 h-11 rounded-xl border border-slate-200 cursor-pointer bg-white p-0.5 flex-shrink-0"
                    aria-label={label}
                />
                <input
                    type="text"
                    value={text}
                    onChange={(e) => commit(e.target.value.trim())}
                    // Anything invalid is simply not committed, so leaving the
                    // box mid-type has to restore the value that is live.
                    onBlur={() => setText(value)}
                    spellCheck={false}
                    className="flex-1 min-w-0 px-3 py-2 rounded-xl border border-slate-200 text-xs font-mono uppercase tracking-wide focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
            </div>
        </Field>
    );
}

function SliderField({ label, value, min, max, step = 1, suffix = '', onChange }) {
    return (
        <Field label={label} hint={`${value}${suffix}`}>
            <input
                type="range"
                min={min} max={max} step={step} value={value}
                onChange={(e) => onChange(Number(e.target.value))}
                className="w-full accent-primary-600"
            />
        </Field>
    );
}

function ChoiceField({ label, value, options, onChange, columns = 3 }) {
    return (
        <Field label={label}>
            <div className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
                {options.map((option) => (
                    <button
                        key={option.value}
                        type="button"
                        onClick={() => onChange(option.value)}
                        className={`px-2 py-2 rounded-xl text-[11px] font-bold border transition
                            ${value === option.value
                                ? 'bg-primary-600 text-white border-primary-600 shadow-sm'
                                : 'bg-white text-slate-600 border-slate-200 hover:border-primary-300'}`}
                    >
                        {option.label}
                    </button>
                ))}
            </div>
        </Field>
    );
}

// ── The live preview ─────────────────────────────────────────────────────

/**
 * A miniature of the real profile: wallpaper, card, avatar, name, bio, two
 * rows and the accent button. Every element here exists on the page proper,
 * and every one of them is drawn by the same class as its full-size twin.
 */
function ThemePreview({ theme, username, avatar }) {
    const { t } = useTranslation();
    const vars = useMemo(() => themeVars(theme), [theme]);
    const shape = resolveTheme(theme).avatar.shape;

    return (
        <div className="relative rounded-2xl overflow-hidden border border-slate-200 pf-scope" style={vars}>
            <ProfileBackdrop theme={theme} inline />

            <div className="relative p-4">
                <div className="pf-card p-4 max-w-[260px] mx-auto">
                    <div className="flex justify-center mb-2">
                        <img
                            src={avatar}
                            alt=""
                            className="w-12 h-12 object-cover pf-avatar ring-2 ring-white/40"
                            style={{ borderRadius: shape === 'circle' ? '9999px' : shape === 'rounded' ? '28%' : '12%' }}
                        />
                    </div>
                    <p className="text-center text-sm font-black pf-heading truncate">{username}</p>
                    <p className="text-center text-[10px] pf-muted mt-0.5 mb-2.5">{t('theme.previewBio')}</p>

                    <div className="flex justify-center mb-2.5">
                        <span className="pf-accent-btn px-3 py-1 text-[10px] font-extrabold">
                            {t('user.contactBtn')}
                        </span>
                    </div>

                    <div className="flex flex-col gap-1.5">
                        {[t('theme.previewRowA'), t('theme.previewRowB')].map((label, index) => (
                            <div key={index} className="pf-row flex items-center gap-2 p-1.5">
                                <span className="w-6 h-6 rounded-md bg-current opacity-15 flex-shrink-0" />
                                <span className="text-[10px] font-bold pf-row-title truncate">{label}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ── Template gallery ─────────────────────────────────────────────────────

/** A template's own colours, arranged the way the page arranges them. */
function PresetSwatch({ theme }) {
    const t = resolveTheme(theme);
    const base = t.background.type === 'gradient'
        ? `linear-gradient(${t.background.angle}deg, ${t.background.color}, ${t.background.color2})`
        : t.background.color;

    return (
        <div className="h-16 rounded-lg overflow-hidden flex items-center justify-center" style={{ background: base }}>
            <div
                className="w-4/5 py-2 px-2 flex flex-col gap-1 items-center"
                style={{
                    background: t.card.style === 'none' ? 'transparent' : t.card.color,
                    borderRadius: Math.min(12, t.card.radius),
                }}
            >
                <span className="h-1.5 w-8 rounded-full" style={{ background: t.text.heading }} />
                <span
                    className="h-2.5 w-full"
                    style={{
                        background: t.button.fill === 'outline' ? 'transparent' : t.button.color,
                        border: `1px solid ${t.button.border}`,
                        borderRadius: Math.min(8, t.button.radius),
                    }}
                />
                <span className="h-1.5 w-6 rounded-full" style={{ background: t.accent }} />
            </div>
        </div>
    );
}

// ── The studio ───────────────────────────────────────────────────────────

const TABS = ['presets', 'background', 'card', 'text', 'buttons'];

function ThemeStudio({ open, onClose, theme, username, avatar, onSaved }) {
    const { t } = useTranslation();
    const [draft, setDraft] = useState(() => resolveTheme(theme));
    const [tab, setTab] = useState('presets');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [pendingFile, setPendingFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);

    // Reopening starts from what is published, not from the abandoned draft of
    // a session that was cancelled.
    useEffect(() => {
        if (open) {
            setDraft(resolveTheme(theme));
            setTab('presets');
            setError('');
        }
    }, [open, theme]);

    // The picker tiles have to be drawn in the faces they are offering, so the
    // whole catalogue loads when that tab is first opened — and only then.
    useEffect(() => {
        if (tab === 'text') FONT_KEYS.forEach(ensureFont);
        else ensureFont(draft.text.font);
    }, [tab, draft.text.font]);

    // A change to any single control is a departure from the template it came
    // from, so the gallery stops claiming that template is still selected.
    const patch = (section, values) => setDraft((prev) => ({
        ...prev,
        preset: 'custom',
        [section]: { ...prev[section], ...values },
    }));
    const patchRoot = (values) => setDraft((prev) => ({ ...prev, preset: 'custom', ...values }));

    const pickPreset = (id) => setDraft((prev) => applyPreset(id, prev));

    const onPickFile = (event) => {
        const file = event.target.files?.[0];
        if (file) setPendingFile(file);
        // Same file twice in a row has to re-fire `change`.
        event.target.value = '';
    };

    /**
     * The wallpaper goes to Cloudflare on pick rather than on save, because a
     * theme stores a URL and there is no URL until the bytes are up there.
     * Choosing the photo also switches the background to `image` — otherwise
     * the upload finishes and the page looks exactly as it did.
     */
    const uploadWallpaper = async (cropped) => {
        setUploading(true);
        setError('');
        try {
            const url = await uploadImage(cropped);
            if (!url) throw new Error('upload_failed');
            patch('background', { image: url, type: 'image' });
            setPendingFile(null);
        } catch (e) {
            setError(t('theme.uploadError'));
        } finally {
            setUploading(false);
        }
    };

    const save = async () => {
        setSaving(true);
        setError('');
        try {
            const res = await api.put('/api/profile/theme/', { theme: draft });
            // The server's copy, not the draft: it clamps and drops, and the
            // screen has to show what was actually stored.
            onSaved?.(res.data);
            onClose();
        } catch (e) {
            setError(t('theme.saveError'));
        } finally {
            setSaving(false);
        }
    };

    if (!open) return null;

    const tabLabel = {
        presets: t('theme.tabPresets'),
        background: t('theme.tabBackground'),
        card: t('theme.tabCard'),
        text: t('theme.tabText'),
        buttons: t('theme.tabButtons'),
    };

    return (<>
        <Modal
            open={open}
            onClose={onClose}
            title={t('theme.title')}
            subtitle={t('theme.subtitle')}
            size="xl"
            footer={
                <div className="flex gap-3">
                    <button type="button" className="btn-secondary flex-1 py-2.5 text-sm" onClick={onClose}>
                        {t('profile.closeBtn')}
                    </button>
                    <button
                        type="button"
                        className="btn-primary flex-1 py-2.5 text-sm flex items-center justify-center gap-2"
                        onClick={save}
                        disabled={saving || uploading}
                    >
                        {saving && <span className="spinner-sm" />}
                        {t('theme.save')}
                    </button>
                </div>
            }
        >
            {/* Sticky so the picture stays put while the controls scroll under
                it — a preview you have to scroll back up to check is one nobody
                checks. */}
            <div className="sticky -top-1 z-10 -mx-1 px-1 pt-1 pb-3 bg-white">
                <ThemePreview theme={draft} username={username} avatar={avatar} />
            </div>

            <div className="flex gap-1 overflow-x-auto custom-scrollbar -mx-1 px-1 pb-3">
                {TABS.map((key) => (
                    <button
                        key={key}
                        type="button"
                        onClick={() => setTab(key)}
                        className={`px-3 py-1.5 rounded-full text-[11px] font-bold whitespace-nowrap transition border
                            ${tab === key
                                ? 'bg-slate-900 text-white border-slate-900'
                                : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'}`}
                    >
                        {tabLabel[key]}
                    </button>
                ))}
            </div>

            {error && (
                <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2 mb-3">
                    {error}
                </p>
            )}

            {tab === 'presets' && (
                <div>
                    <p className="text-[11px] text-slate-500 mb-3 leading-relaxed">{t('theme.presetsHint')}</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                        {PRESETS.map((item) => (
                            <button
                                key={item.id}
                                type="button"
                                onClick={() => pickPreset(item.id)}
                                className={`text-left rounded-xl p-1.5 border-2 transition
                                    ${draft.preset === item.id
                                        ? 'border-primary-500 bg-primary-50'
                                        : 'border-slate-200 hover:border-slate-300 bg-white'}`}
                            >
                                <PresetSwatch theme={item.theme} />
                                <p className="text-[10px] font-bold text-slate-600 mt-1.5 px-0.5 truncate">
                                    {t(item.labelKey)}
                                </p>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {tab === 'background' && (
                <div>
                    <ChoiceField
                        label={t('theme.bgType')}
                        value={draft.background.type}
                        onChange={(value) => patch('background', { type: value })}
                        options={[
                            { value: 'solid', label: t('theme.bgSolid') },
                            { value: 'gradient', label: t('theme.bgGradient') },
                            { value: 'image', label: t('theme.bgImage') },
                        ]}
                    />

                    <ColorField
                        label={draft.background.type === 'gradient' ? t('theme.bgColorFrom') : t('theme.bgColor')}
                        value={draft.background.color}
                        onChange={(value) => patch('background', { color: value })}
                    />

                    {draft.background.type === 'gradient' && (<>
                        <ColorField
                            label={t('theme.bgColorTo')}
                            value={draft.background.color2}
                            onChange={(value) => patch('background', { color2: value })}
                        />
                        <SliderField
                            label={t('theme.bgAngle')} suffix="°"
                            value={draft.background.angle} min={0} max={360} step={5}
                            onChange={(value) => patch('background', { angle: value })}
                        />
                    </>)}

                    {draft.background.type === 'image' && (<>
                        <Field label={t('theme.bgPhoto')} hint={t('theme.bgPhotoHint')}>
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => fileRef.current?.click()}
                                    disabled={uploading}
                                    className="flex-1 px-3 py-2.5 rounded-xl border border-dashed border-slate-300 text-xs font-bold text-slate-600 hover:border-primary-400 hover:text-primary-700 transition disabled:opacity-50"
                                >
                                    {uploading ? t('theme.uploading')
                                        : draft.background.image ? t('theme.bgPhotoReplace') : t('theme.bgPhotoPick')}
                                </button>
                                {draft.background.image && (
                                    <button
                                        type="button"
                                        onClick={() => patch('background', { image: null, type: 'solid' })}
                                        className="px-3 py-2.5 rounded-xl border border-slate-200 text-xs font-bold text-slate-500 hover:text-red-600 hover:border-red-200 transition"
                                    >
                                        {t('theme.remove')}
                                    </button>
                                )}
                            </div>
                            <input ref={fileRef} type="file" accept="image/*" onChange={onPickFile} className="hidden" />
                        </Field>

                        <SliderField
                            label={t('theme.bgBlur')} suffix="px"
                            value={draft.background.blur} min={0} max={40}
                            onChange={(value) => patch('background', { blur: value })}
                        />
                        <SliderField
                            label={t('theme.bgDim')} suffix="%"
                            value={draft.background.dim} min={0} max={85}
                            onChange={(value) => patch('background', { dim: value })}
                        />
                    </>)}

                    {draft.background.type !== 'image' && (
                        <ChoiceField
                            label={t('theme.bgPattern')}
                            value={draft.background.pattern}
                            columns={5}
                            onChange={(value) => patch('background', { pattern: value })}
                            options={PATTERNS.map((value) => ({ value, label: t(`theme.pattern_${value}`) }))}
                        />
                    )}
                </div>
            )}

            {tab === 'card' && (
                <div>
                    <ChoiceField
                        label={t('theme.cardStyle')}
                        value={draft.card.style}
                        onChange={(value) => patch('card', { style: value })}
                        options={[
                            { value: 'solid', label: t('theme.cardSolid') },
                            { value: 'glass', label: t('theme.cardGlass') },
                            { value: 'none', label: t('theme.cardNone') },
                        ]}
                    />

                    {draft.card.style !== 'none' && (<>
                        <ColorField
                            label={t('theme.cardColor')}
                            value={draft.card.color}
                            onChange={(value) => patch('card', { color: value })}
                        />
                        <ColorField
                            label={t('theme.cardBorder')}
                            value={draft.card.border}
                            onChange={(value) => patch('card', { border: value })}
                        />
                        <SliderField
                            label={t('theme.cardOpacity')} suffix="%"
                            value={draft.card.opacity} min={25} max={100}
                            onChange={(value) => patch('card', { opacity: value })}
                        />
                    </>)}

                    <SliderField
                        label={t('theme.cardRadius')} suffix="px"
                        value={draft.card.radius} min={0} max={48}
                        onChange={(value) => patch('card', { radius: value })}
                    />

                    <ChoiceField
                        label={t('theme.avatarShape')}
                        value={draft.avatar.shape}
                        onChange={(value) => patch('avatar', { shape: value })}
                        options={[
                            { value: 'circle', label: t('theme.avatarCircle') },
                            { value: 'rounded', label: t('theme.avatarRounded') },
                            { value: 'square', label: t('theme.avatarSquare') },
                        ]}
                    />
                </div>
            )}

            {tab === 'text' && (
                <div>
                    <Field label={t('theme.font')}>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                            {FONT_KEYS.map((key) => (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => patch('text', { font: key })}
                                    style={{ fontFamily: FONTS[key].stack }}
                                    className={`px-2 py-2.5 rounded-xl border text-sm truncate transition
                                        ${draft.text.font === key
                                            ? 'border-primary-500 bg-primary-50 text-primary-800'
                                            : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'}`}
                                >
                                    {FONTS[key].label}
                                </button>
                            ))}
                        </div>
                    </Field>

                    <ColorField
                        label={t('theme.textHeading')}
                        value={draft.text.heading}
                        onChange={(value) => patch('text', { heading: value })}
                    />
                    <ColorField
                        label={t('theme.textBody')}
                        value={draft.text.body}
                        onChange={(value) => patch('text', { body: value })}
                    />
                    <ColorField
                        label={t('theme.textMuted')}
                        value={draft.text.muted}
                        onChange={(value) => patch('text', { muted: value })}
                    />
                </div>
            )}

            {tab === 'buttons' && (
                <div>
                    <p className="text-[11px] text-slate-500 mb-3 leading-relaxed">{t('theme.buttonsHint')}</p>

                    <ChoiceField
                        label={t('theme.btnFill')}
                        value={draft.button.fill}
                        onChange={(value) => patch('button', { fill: value })}
                        options={[
                            { value: 'solid', label: t('theme.fillSolid') },
                            { value: 'outline', label: t('theme.fillOutline') },
                            { value: 'glass', label: t('theme.fillGlass') },
                        ]}
                    />
                    <ChoiceField
                        label={t('theme.btnShadow')}
                        value={draft.button.shadow}
                        onChange={(value) => patch('button', { shadow: value })}
                        options={[
                            { value: 'none', label: t('theme.shadowNone') },
                            { value: 'soft', label: t('theme.shadowSoft') },
                            { value: 'hard', label: t('theme.shadowHard') },
                        ]}
                    />
                    <SliderField
                        label={t('theme.btnRadius')} suffix="px"
                        value={draft.button.radius} min={0} max={28}
                        onChange={(value) => patch('button', { radius: value })}
                    />

                    <ColorField
                        label={t('theme.btnColor')}
                        value={draft.button.color}
                        onChange={(value) => patch('button', { color: value })}
                    />
                    <ColorField
                        label={t('theme.btnText')}
                        value={draft.button.text}
                        onChange={(value) => patch('button', { text: value })}
                    />
                    <ColorField
                        label={t('theme.btnBorder')}
                        value={draft.button.border}
                        onChange={(value) => patch('button', { border: value })}
                    />

                    <hr className="border-slate-100 my-4" />

                    <ColorField
                        label={t('theme.accent')}
                        value={draft.accent}
                        onChange={(value) => patchRoot({ accent: value })}
                    />
                    <ColorField
                        label={t('theme.accentText')}
                        value={draft.accent_text}
                        onChange={(value) => patchRoot({ accent_text: value })}
                    />
                </div>
            )}
        </Modal>

        {/* The wallpaper is framed full-bleed on a phone, so an uncropped
            upload publishes as whatever band of itself happens to fit — the
            same reason the media-kit cover is cropped before it goes up. */}
        {pendingFile && (
            <ImageCropModal
                file={pendingFile}
                aspect={WALLPAPER_ASPECT}
                title={t('theme.cropTitle')}
                hint={t('theme.cropHint')}
                busy={uploading}
                onCancel={() => setPendingFile(null)}
                onCropped={uploadWallpaper}
            />
        )}
    </>);
}

export default ThemeStudio;
